import React from "react";
import { Typography } from "@mui/material";

const TroubleshootingGuide = () => {
  return (
    <>
      <Typography variant="h6" className="section-heading" color="secondary">
        Common Chart Reading Issues
      </Typography>
      <ul className="feature-list">
        <li>
          <span className="feature-item-title">Lost Your Place</span>
          <span className="feature-item-description">
            Use a row marker (sticky note or highlighter) above your current row. Some knitters use
            a magnetic board with a ruler to track rows.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Wrong Reading Direction</span>
          <span className="feature-item-description">
            Remember: RS rows read right to left, WS rows read left to right (for flat knitting). In
            the round, always read right to left.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Stitch Count Doesn't Match</span>
          <span className="feature-item-description">
            Count the stitches in the chart row before you start knitting. Make sure you have the
            correct number on your needles. Check for missed increases or decreases.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Pattern Looks Wrong</span>
          <span className="feature-item-description">
            Compare your work to the chart frequently. It's easier to fix mistakes if you catch
            them within a few rows.
          </span>
        </li>
      </ul>

      <Typography variant="h6" className="section-heading" color="secondary">
        Common Written Pattern Issues
      </Typography>
      <ul className="feature-list">
        <li>
          <span className="feature-item-title">Unclear Abbreviations</span>
          <span className="feature-item-description">
            Always check the pattern's abbreviation list. If a pattern doesn't include one, refer to
            Craft Yarn Council standards or ask for clarification.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Confusing Repeats</span>
          <span className="feature-item-description">
            Break down repeat instructions step by step. Write out the first repeat fully if needed,
            then identify the repeating section.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Wrong Size</span>
          <span className="feature-item-description">
            Check your gauge! If your gauge doesn't match the pattern, your finished item will be
            the wrong size. Make a gauge swatch before starting.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Lost in Long Instructions</span>
          <span className="feature-item-description">
            Use a row counter or mark off completed rows. Some knitters highlight or check off each
            row as they complete it.
          </span>
        </li>
      </ul>

      <Typography variant="h6" className="section-heading" color="secondary">
        Fixing Mistakes
      </Typography>
      <ul className="feature-list">
        <li>
          <span className="feature-item-title">Catch Mistakes Early</span>
          <span className="feature-item-description">
            Check your work every few rows. It's much easier to fix a mistake in the last row than
            several rows down.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Tinking (Unknitting)</span>
          <span className="feature-item-description">
            Carefully unknit stitch by stitch back to your mistake. This preserves your yarn and is
            less stressful than ripping out entire rows.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Ripping Out</span>
          <span className="feature-item-description">
            Sometimes you need to rip out (frog) several rows. Use a lifeline (thread a contrasting
            yarn through a row) before ripping to make it easier to pick up stitches.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Lifelines</span>
          <span className="feature-item-description">
            Thread a smooth, contrasting yarn through a row of stitches before attempting complex
            sections. If you need to rip back, you can easily pick up from the lifeline.
          </span>
        </li>
      </ul>

      <Typography variant="h6" className="section-heading" color="secondary">
        Tool-Specific Issues
      </Typography>
      <ul className="feature-list">
        <li>
          <span className="feature-item-title">Chart Not Exporting</span>
          <span className="feature-item-description">
            Make sure you've added a pattern title. Large charts may take longer to export—be
            patient and don't close the browser tab.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Written Instructions Look Wrong</span>
          <span className="feature-item-description">
            Check your chart for empty cells or incorrect symbols. Written instructions are
            generated directly from your chart, so any issues in the chart will appear in the
            instructions.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Can't See Full Chart</span>
          <span className="feature-item-description">
            Use zoom controls to adjust the view. Zoom out to see the big picture, or zoom in for
            detailed work. The 'Fit' button automatically adjusts zoom to show the entire chart.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Accidentally Cleared Grid</span>
          <span className="feature-item-description">
            If you haven't exported your pattern, you may have lost your work. Always export as JSON
            regularly to save your progress!
          </span>
        </li>
      </ul>

      <Typography variant="h6" className="section-heading" color="secondary">
        Getting Help
      </Typography>
      <Typography variant="body1">
        If you're stuck, try these resources:
      </Typography>
      <ul className="feature-list">
        <li>
          <span className="feature-item-title">Pattern Support</span>
          <span className="feature-item-description">
            Contact the pattern designer if you have questions about a specific pattern. Many
            designers are happy to help!
          </span>
        </li>
        <li>
          <span className="feature-item-title">Online Communities</span>
          <span className="feature-item-description">
            Join knitting forums, Ravelry groups, or social media communities. Other knitters are
            usually happy to help troubleshoot.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Local Yarn Shops</span>
          <span className="feature-item-description">
            Many yarn shops offer help sessions or classes. Staff can often help you understand
            tricky patterns or techniques.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Video Tutorials</span>
          <span className="feature-item-description">
            Search for specific techniques or pattern types on YouTube. Seeing someone else do it
            can make all the difference.
          </span>
        </li>
      </ul>
    </>
  );
};

export default TroubleshootingGuide;

